import React, { useState } from 'react';
import './TablePagination.scss';

interface TablePaginationProps {
  currentPage: number;
  totalPages: number;
  rowsPerPage: number;
  totalRows: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rows: number) => void;
}

const TablePagination: React.FC<TablePaginationProps> = ({
                                                           currentPage,
                                                           totalPages,
                                                           rowsPerPage,
                                                           totalRows,
                                                           onPageChange,
                                                           onRowsPerPageChange
                                                         }) => {
  const baseClass = 'TablePagination';
  const [goToPage, setGoToPage] = useState('');

  const ArrowIcon = ({ direction }: { direction: 'left' | 'right' }) => (
    <svg
      width="8"
      height="7"
      viewBox="0 0 8 7"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={`${baseClass}__arrow ${baseClass}__arrow--${direction}`}
    >
      <path
        d="M0 3.5L7 0L7 7L0 3.5Z"
        fill="#C4CDD5"
        transform={direction === 'left' ? 'rotate(180 4 3.5)' : ''}
      />
    </svg>
  );

  const handleRowsChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newRowsPerPage = event.target.value === 'all' ? totalRows : parseInt(event.target.value);
    onRowsPerPageChange(newRowsPerPage);
    onPageChange(1); // Reset to first page
  };

  const handleGoToPage = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      const pageNumber = parseInt(goToPage);
      if (pageNumber >= 1 && pageNumber <= totalPages) {
        onPageChange(pageNumber);
        setGoToPage('');
      }
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  };

  // Generate page numbers
  const generatePageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        pages.push(1, 2, 3, 4, 5, '...', totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1, '...', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
      }
    }

    return pages;
  };

  const pageNumbers = generatePageNumbers();
  const startRow = (currentPage - 1) * rowsPerPage + 1;
  const endRow = Math.min(currentPage * rowsPerPage, totalRows);

  return (
    <div className={baseClass}>
      {/* Show rows selector */}
      <div className={`${baseClass}__show-section`}>
        <span className={`${baseClass}__show-label`}>Show rows:</span>
        <select
          className={`${baseClass}__rows-selector`}
          value={rowsPerPage === totalRows ? 'all' : rowsPerPage}
          onChange={handleRowsChange}
        >
          <option value={5}>5</option>
          <option value={10}>10</option>
          <option value={20}>20</option>
          <option value="all">all</option>
        </select>
      </div>

      {/* Pagination controls */}
      <div className={`${baseClass}__pagination`}>
        <button
          className={`${baseClass}__nav-btn ${currentPage === 1 ? `${baseClass}__nav-btn--disabled` : ''}`}
          onClick={handlePrevPage}
          disabled={currentPage === 1}
        >
          <ArrowIcon direction="left" />
        </button>

        <div className={`${baseClass}__page-info`}>
          {pageNumbers.map((page, index) => (
            <React.Fragment key={index}>
              {page === '...' ? (
                <span className={`${baseClass}__ellipsis`}>...</span>
              ) : (
                <button
                  className={`${baseClass}__page-btn ${page === currentPage ? `${baseClass}__page-btn--active` : ''}`}
                  onClick={() => onPageChange(page as number)}
                >
                  {page}
                </button>
              )}
            </React.Fragment>
          ))}
        </div>

        <button
          className={`${baseClass}__nav-btn ${currentPage === totalPages ? `${baseClass}__nav-btn--disabled` : ''}`}
          onClick={handleNextPage}
          disabled={currentPage === totalPages}
        >
          <ArrowIcon direction="right" />
        </button>
      </div>

      {/* Go to page */}
      <div className={`${baseClass}__goto-section`}>
        <span className={`${baseClass}__goto-label`}>Go to page:</span>
        <input
          type="number"
          className={`${baseClass}__goto-input`}
          placeholder="..."
          value={goToPage}
          onChange={(e) => setGoToPage(e.target.value)}
          onKeyPress={handleGoToPage}
          min={1}
          max={totalPages}
        />
      </div>
    </div>
  );
};

export default TablePagination;